const { TokenType, Keywords } = require('./keywords');

class Parser {
    constructor(tokens) {
        this.tokens = tokens;
        this.position = 0;
    }

    parse() {
        // Program should start with PROGRAM_START (hi / नमस्ते)
        this.consume(TokenType.KEYWORD, Keywords.PROGRAM_START, "Program must start with proper start keyword");

        const body = [];
        while (!this.check(TokenType.KEYWORD, Keywords.PROGRAM_END) && !this.isAtEnd()) {
            body.push(this.parseStatement());
        }

        this.consume(TokenType.KEYWORD, Keywords.PROGRAM_END, "Program must end with proper end keyword");
        return { type: 'Program', body };
    }

    parseStatement() {
        if (this.matchKeyword(Keywords.PRINT)) {
            return this.parsePrintStatement();
        }
        if (this.matchKeyword(Keywords.VARIABLE)) {
            return this.parseVariableDeclaration();
        }
        if (this.matchKeyword(Keywords.IF)) {
            return this.parseIfStatement();
        }
        if (this.matchKeyword(Keywords.UNTIL)) {
            return this.parseUntilStatement();
        }
        if (this.matchKeyword(Keywords.FOR)) {
            return this.parseForStatement();
        }
        if (this.matchKeyword(Keywords.BREAK)) {
            this.consume(TokenType.PUNCTUATION, ';', "Expected ';' after break");
            return { type: 'BreakStatement' };
        }
        if (this.matchKeyword(Keywords.CONTINUE)) {
            this.consume(TokenType.PUNCTUATION, ';', "Expected ';' after continue");
            return { type: 'ContinueStatement' };
        }
        // Identifier start (Assignment)
        if (this.check(TokenType.IDENTIFIER)) {
            return this.parseAssignment();
        }

        throw new Error(`Unexpected token at line ${this.peek().line}: ${this.peek().value}`);
    }

    parsePrintStatement() {
        const expression = this.parseExpression();
        this.consume(TokenType.PUNCTUATION, ';', "Expected ';' after print statement");
        return { type: 'PrintStatement', expression };
    }

    parseVariableDeclaration() {
        const name = this.consume(TokenType.IDENTIFIER, null, "Expected variable name").value;
        this.consume(TokenType.OPERATOR, '=', "Expected '=' in variable declaration");
        const value = this.parseExpression();
        this.consume(TokenType.PUNCTUATION, ';', "Expected ';' after variable declaration");
        return { type: 'VariableDeclaration', name, value };
    }

    parseAssignment() {
        const stmt = this.parseAssignmentExpression();
        this.consume(TokenType.PUNCTUATION, ';', "Expected ';' after assignment");
        return stmt;
    }

    parseAssignmentExpression() {
        const name = this.consume(TokenType.IDENTIFIER, null, "Expected variable name").value;

        let index = null;
        if (this.match(TokenType.PUNCTUATION, '[')) {
            index = this.parseExpression();
            this.consume(TokenType.PUNCTUATION, ']', "Expected ']' after index in assignment");
        }

        let operator = '=';

        if (this.match(TokenType.OPERATOR, '+=')) {
            operator = '+=';
        } else {
            this.consume(TokenType.OPERATOR, '=', "Expected '=' or '+=' in assignment");
        }

        const value = this.parseExpression();

        if (index !== null) {
            return { type: 'MemberAssignment', object: name, index, value, operator };
        }

        return { type: 'Assignment', name, value, operator };
    }

    parseIfStatement() {
        this.consume(TokenType.PUNCTUATION, '(', "Expected '(' after 'if'");
        const condition = this.parseExpression();
        this.consume(TokenType.PUNCTUATION, ')', "Expected ')' after if condition");

        this.consume(TokenType.PUNCTUATION, '{', "Expected '{' before if body");
        const thenBranch = [];
        while (!this.check(TokenType.PUNCTUATION, '}') && !this.isAtEnd()) {
            thenBranch.push(this.parseStatement());
        }
        this.consume(TokenType.PUNCTUATION, '}', "Expected '}' after if body");

        let elseBranch = null;
        if (this.matchKeyword(Keywords.ELSE)) {
            if (this.matchKeyword(Keywords.IF)) {
                elseBranch = [this.parseIfStatement()];
            } else {
                this.consume(TokenType.PUNCTUATION, '{', "Expected '{' before else body");
                elseBranch = [];
                while (!this.check(TokenType.PUNCTUATION, '}') && !this.isAtEnd()) {
                    elseBranch.push(this.parseStatement());
                }
                this.consume(TokenType.PUNCTUATION, '}', "Expected '}' after else body");
            }
        }

        return { type: 'IfStatement', condition, thenBranch, elseBranch };
    }

    parseUntilStatement() { // until (cond) { ... }
        this.consume(TokenType.PUNCTUATION, '(', "Expected '(' after 'until'");
        const condition = this.parseExpression();
        this.consume(TokenType.PUNCTUATION, ')', "Expected ')' after until condition");

        this.consume(TokenType.PUNCTUATION, '{', "Expected '{' before loop body");
        const body = [];
        while (!this.check(TokenType.PUNCTUATION, '}') && !this.isAtEnd()) {
            body.push(this.parseStatement());
        }
        this.consume(TokenType.PUNCTUATION, '}', "Expected '}' after loop body");

        return { type: 'UntilStatement', condition, body };
    }

    parseForStatement() {
        this.consume(TokenType.PUNCTUATION, '(', "Expected '(' after 'for'");

        let init = null;
        if (!this.check(TokenType.PUNCTUATION, ';')) {
            if (this.checkKeyword(Keywords.VARIABLE)) {
                this.advance(); // Consume VARIABLE keyword
                init = this.parseVariableDeclaration();
            } else {
                init = this.parseAssignment();
            }
        } else {
            this.consume(TokenType.PUNCTUATION, ';', "Expected ';'");
        }

        const condition = this.parseExpression();
        this.consume(TokenType.PUNCTUATION, ';', "Expected ';' after loop condition");

        let update = null;
        if (!this.check(TokenType.PUNCTUATION, ')')) {
            update = this.parseAssignmentExpression();
        }
        this.consume(TokenType.PUNCTUATION, ')', "Expected ')' after loop clauses");

        this.consume(TokenType.PUNCTUATION, '{', "Expected '{' before loop body");
        const body = [];
        while (!this.check(TokenType.PUNCTUATION, '}') && !this.isAtEnd()) {
            body.push(this.parseStatement());
        }
        this.consume(TokenType.PUNCTUATION, '}', "Expected '}' after loop body");

        return { type: 'ForStatement', init, condition, update, body };
    }

    parseExpression() {
        return this.logicalOr();
    }

    logicalOr() {
        let expr = this.logicalAnd();

        while (this.matchKeyword(Keywords.OR)) {
            const operator = this.previous().value;
            const right = this.logicalAnd();
            expr = { type: 'LogicalExpression', left: expr, operator, right };
        }
        return expr;
    }

    logicalAnd() {
        let expr = this.equality();

        while (this.matchKeyword(Keywords.AND)) {
            const operator = this.previous().value;
            const right = this.equality();
            expr = { type: 'LogicalExpression', left: expr, operator, right };
        }
        return expr;
    }

    equality() {
        let expr = this.comparison();

        while (this.match(TokenType.OPERATOR, '==') || this.match(TokenType.OPERATOR, '!=')) {
            const operator = this.previous().value;
            const right = this.comparison();
            expr = { type: 'BinaryExpression', left: expr, operator, right };
        }
        return expr;
    }

    comparison() {
        let expr = this.term();

        while (this.match(TokenType.OPERATOR, '>') || this.match(TokenType.OPERATOR, '>=') ||
            this.match(TokenType.OPERATOR, '<') || this.match(TokenType.OPERATOR, '<=')) {
            const operator = this.previous().value;
            const right = this.term();
            expr = { type: 'BinaryExpression', left: expr, operator, right };
        }
        return expr;
    }

    term() {
        let expr = this.factor();

        while (this.match(TokenType.OPERATOR, '-') || this.match(TokenType.OPERATOR, '+')) {
            const operator = this.previous().value;
            const right = this.factor();
            expr = { type: 'BinaryExpression', left: expr, operator, right };
        }
        return expr;
    }

    factor() {
        let expr = this.unary();

        while (this.match(TokenType.OPERATOR, '/') || this.match(TokenType.OPERATOR, '*') || this.match(TokenType.OPERATOR, '%')) {
            const operator = this.previous().value;
            const right = this.unary();
            expr = { type: 'BinaryExpression', left: expr, operator, right };
        }
        return expr;
    }

    unary() {
        if (this.match(TokenType.OPERATOR, '!') || this.match(TokenType.OPERATOR, '-')) {
            const operator = this.previous().value;
            const right = this.unary();
            return { type: 'UnaryExpression', operator, right };
        }

        return this.call();
    }

    call() {
        let expr = this.primary();

        while (true) {
            if (this.match(TokenType.PUNCTUATION, '[')) {
                const index = this.parseExpression();
                this.consume(TokenType.PUNCTUATION, ']', "Expected ']' after index");
                expr = { type: 'MemberExpression', object: expr, index };
            } else if (this.match(TokenType.PUNCTUATION, '.')) {
                const property = this.consume(TokenType.KEYWORD, null, "Expected property name after '.'").value;
                expr = { type: 'PropertyExpression', object: expr, property };
            } else {
                break;
            }
        }
        return expr;
    }

    primary() {
        if (this.matchKeyword(Keywords.FALSE)) return { type: 'Literal', value: false };
        if (this.matchKeyword(Keywords.TRUE)) return { type: 'Literal', value: true };
        if (this.match(TokenType.NUMBER)) return { type: 'Literal', value: this.previous().value };
        if (this.match(TokenType.STRING)) return { type: 'Literal', value: this.previous().value };

        if (this.match(TokenType.IDENTIFIER)) {
            return { type: 'Identifier', name: this.previous().value };
        }

        if (this.match(TokenType.PUNCTUATION, '(')) {
            const expr = this.parseExpression();
            this.consume(TokenType.PUNCTUATION, ')', "Expected ')' after expression");
            return expr;
        }

        if (this.matchKeyword(Keywords.INPUT)) {
            let prompt = null;
            // Check if there is a prompt string or expression following the INPUT keyword
            if (!this.check(TokenType.PUNCTUATION, ';') && !this.check(TokenType.EOF) &&
                !this.check(TokenType.PUNCTUATION, ')')) {
                // To avoid parsing an unrelated expression, we only accept string literals or identifiers as simple prompts
                // Alternatively, just parse an expression and hope for the best
                try {
                    prompt = this.parseExpression();
                } catch (e) {
                    prompt = null; // Ignore if it's not a valid expression
                }
            }
            return { type: 'InputExpression', prompt };
        }

        if (this.match(TokenType.PUNCTUATION, '[')) {
            const elements = [];
            if (!this.check(TokenType.PUNCTUATION, ']')) {
                do {
                    elements.push(this.parseExpression());
                } while (this.match(TokenType.PUNCTUATION, ','));
            }
            this.consume(TokenType.PUNCTUATION, ']', "Expected ']' after array elements");
            return { type: 'ArrayLiteral', elements };
        }

        throw new Error(`Expected expression but found ${this.peek().value} at line ${this.peek().line}`);
    }

    // Helper methods
    match(type, value) {
        if (this.check(type, value)) {
            this.advance();
            return true;
        }
        return false;
    }

    matchKeyword(keywordId) {
        if (this.checkKeyword(keywordId)) {
            this.advance();
            return true;
        }
        return false;
    }

    check(type, value) {
        if (this.isAtEnd()) return false;
        const token = this.peek();
        if (token.type !== type) return false;
        if (value !== undefined && value !== null && token.value !== value) return false;
        return true;
    }

    checkKeyword(keywordId) {
        if (this.isAtEnd()) return false;
        const token = this.peek();
        return token.type === TokenType.KEYWORD && token.value === keywordId;
    }

    advance() {
        if (!this.isAtEnd()) this.position++;
        return this.previous();
    }

    isAtEnd() {
        return this.peek().type === TokenType.EOF;
    }

    peek() {
        return this.tokens[this.position];
    }

    previous() {
        return this.tokens[this.position - 1];
    }

    consume(type, value, message) {
        if (this.check(type, value)) {
            return this.advance();
        }
        throw new Error(message + ` at line ${this.peek().line}. Found: ${this.peek().value}`);
    }
}

module.exports = Parser;
